import { DataRefreshEvent } from "@fp/admin-api-client";
import { Button } from "@fp/ui/components/base/buttons/button";
import { Select, SelectItem } from "@fp/ui/components/base/select/select";
import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "@/lib/axios";
import { useDataRefresh } from "@/hooks/useSignalR";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { useUserContext } from "@/components/user-context";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";

interface PendingJoinRequestDto {
  id: string;
  requestingUserId: string;
  requestingUserEmail: string;
  requestingUserFirstName: string | null;
  requestingUserLastName: string | null;
  organizationId: string;
  organizationName: string;
  invitationType: "Unspecified" | "Staff" | "Talent";
  createdDate: string;
  isAlreadyStaff: boolean;
  isAlreadyTalent: boolean;
}

interface ApproveJoinRequestDto {
  roleCode: string;
}

const STAFF_ROLE_OPTIONS = [
  { value: "ORG_MANAGER", label: "Manager" },
  { value: "ORG_ADMIN", label: "Admin" },
];

const TALENT_ROLE = "ORG_TALENT";

type SearchParams = {
  orgId?: string;
};

export const Route = createFileRoute("/join-requests")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const orgId = search.orgId as string | undefined;
    return orgId ? { orgId } : {};
  },
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const { orgId, setOrgId, organizations } = useUserContext();
  const { orgId: requestedOrgId } = Route.useSearch();

  useEffect(() => {
    if (requestedOrgId && requestedOrgId !== orgId && organizations.some(o => o.id === requestedOrgId)) {
      setOrgId(requestedOrgId);
    }
  }, [requestedOrgId]);
  const [selectedRoles, setSelectedRoles] = useState<Record<string, string>>(
    {},
  );
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [denyingId, setDenyingId] = useState<string | null>(null);
  const [denyModalRequest, setDenyModalRequest] = useState<{ id: string; name: string } | null>(null);

  const { data: pendingRequests, isLoading } = useQuery({
    queryKey: ["pending-join-requests", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const response = await axios.get<PendingJoinRequestDto[]>(
        `/api/join-requests/organization/${orgId}/pending`,
      );
      return response.data;
    },
    enabled: !!orgId,
  });

  const invalidateJoinRequests = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["pending-join-requests", orgId] });
  }, [queryClient, orgId]);

  useDataRefresh(DataRefreshEvent.JoinRequestCreated, invalidateJoinRequests);
  useDataRefresh(DataRefreshEvent.JoinRequestApproved, invalidateJoinRequests);

  const approveMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: ApproveJoinRequestDto;
    }) => {
      const response = await axios.post(
        `/api/join-requests/${id}/approve`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pending-join-requests", orgId],
      });
      setApprovingId(null);
    },
    onError: () => {
      toast.error("Failed to approve request. Please try again.");
      setApprovingId(null);
    },
  });

  const denyMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.post(`/api/join-requests/${id}/deny`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pending-join-requests", orgId],
      });
      setDenyingId(null);
    },
    onError: () => {
      toast.error("Failed to deny request. Please try again.");
      setDenyingId(null);
    },
  });

  const handleApprove = (request: PendingJoinRequestDto) => {
    const roleCode = request.invitationType === "Talent"
      ? TALENT_ROLE
      : selectedRoles[request.id] || "ORG_MANAGER";
    setApprovingId(request.id);
    approveMutation.mutate({
      id: request.id,
      data: { roleCode },
    });
  };

  const handleDeny = (requestId: string, name: string) => {
    setDenyModalRequest({ id: requestId, name });
  };

  const handleDenyConfirm = () => {
    if (denyModalRequest) {
      setDenyingId(denyModalRequest.id);
      denyMutation.mutate(denyModalRequest.id);
      setDenyModalRequest(null);
    }
  };

  const getDisplayName = (request: PendingJoinRequestDto) => {
    if (request.requestingUserFirstName || request.requestingUserLastName) {
      return `${request.requestingUserFirstName || ""} ${request.requestingUserLastName || ""}`.trim();
    }
    return request.requestingUserEmail;
  };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Pending Requests"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer className="space-y-4">
        {isLoading ? (
          <PageLoading />
        ) : pendingRequests && pendingRequests.length > 0 ? (
          <div className="space-y-4">
            {pendingRequests.map((request) => (
              <div
                key={request.id}
                className="bg-white border border-gray-200 rounded-lg p-4 space-y-4"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-gray-900">
                      {getDisplayName(request)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {request.requestingUserEmail}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Requested on {formatLocalDate(request.createdDate, "dateWithYear")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-700">
                      {request.organizationName}
                    </div>
                    <span className={`inline-block mt-2 text-sm font-medium px-3 py-1 rounded-full ${request.invitationType === "Talent" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>
                      {request.invitationType === "Talent" ? "Talent Member" : "Staff"}
                    </span>
                    {(request.isAlreadyStaff || request.isAlreadyTalent) && (
                      <div className="mt-1 text-xs text-gray-500">
                        Already {request.isAlreadyStaff ? "staff" : "a talent member"}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4 space-y-3">
                  {request.invitationType !== "Talent" && (
                    <div className="w-32">
                      <label
                        className="block text-sm font-medium text-gray-700 mb-1"
                        htmlFor={`role-select-${request.id}`}
                      >
                        Role
                      </label>
                      <Select
                        id={`role-select-${request.id}`}
                        selectedKey={selectedRoles[request.id] || "ORG_MANAGER"}
                        onSelectionChange={(key: React.Key | null) =>
                          setSelectedRoles((prev) => ({
                            ...prev,
                            [request.id]: key as string,
                          }))
                        }
                      >
                        {STAFF_ROLE_OPTIONS.map((role) => (
                          <SelectItem key={role.value} id={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      color="secondary-destructive"
                      onClick={() => handleDeny(request.id, getDisplayName(request))}
                      isLoading={denyingId === request.id}
                      isDisabled={
                        approvingId === request.id || denyingId === request.id
                      }
                      className="flex-1"
                    >
                      Deny
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => handleApprove(request)}
                      isLoading={approvingId === request.id}
                      isDisabled={
                        approvingId === request.id || denyingId === request.id
                      }
                      className="flex-1"
                    >
                      Approve
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-2">No pending requests</div>
            <p className="text-sm text-gray-400">
              When someone requests to join your organization, they'll appear
              here.
            </p>
          </div>
        )}
      </PageContentContainer>
      <ConfirmationModal
        isOpen={!!denyModalRequest}
        onClose={() => setDenyModalRequest(null)}
        onConfirm={handleDenyConfirm}
        title="Deny Request"
        message={`Are you sure you want to deny the request from ${denyModalRequest?.name}?`}
        confirmText="Deny"
        cancelText="Cancel"
        isLoading={!!denyingId}
      />
    </AdminLayout>
  );
}
